<template>
  <div :class="['tab-container', alignmentClass]">
    <div
      v-if="hasTwoContents"
      class="tab-header"
      :class="{ 'tab-header--sticky': stickyTabHeader }"
    >
      <button
        :class="{ active: activeTab === 1 }"
        @click="activeTab = 1"
      >
        {{ tabOneText }}
      </button>
      <button
        :class="{ active: activeTab === 2 }"
        @click="activeTab = 2"
      >
        {{ tabTwoText }}
      </button>
    </div>
    <div class="tab-content">
      <slot
        v-if="activeTab === 1 || !hasTwoContents"
        name="content1"
      ></slot>
      <slot
        v-if="activeTab === 2"
        name="content2"
      ></slot>
    </div>
  </div>
</template>

<script lang="ts">
  import { defineComponent, computed } from 'vue'

  export default defineComponent({
    name: 'TabContainer',
    props: {
      modelValue: {
        type: Number,
        default: 1
      },
      hasTwoContents: {
        type: Boolean,
        required: true,
      },
      tabOneText: {
        type: String,
        default: 'Tab 1',
      },
      tabTwoText: {
        type: String,
        default: 'Tab 2',
      },
      alignment: {
        type: String,
        default: 'center',
        validator: (value: string) => ['left', 'center'].includes(value),
      },
      stickyTabHeader: {
        type: Boolean,
        default: false,
        required: false,
      }
    },
    emits: ['update:modelValue'],
    setup(props, { emit }) {
      const activeTab = computed({
        get: () => props.modelValue,
        set: (value) => emit('update:modelValue', value)
      })

      const alignmentClass = computed(() => {
        return props.alignment === 'left' ? 'left-aligned' : 'center-aligned'
      })

      return {
        activeTab,
        alignmentClass,
      }
    },
  })
</script>

<style lang="scss" scoped>
  .tab-container {
    width: 100%;
    background-color: transparent;
    text-align: center;

    &.left-aligned {
      text-align: left;
    }
  }

  .tab-header {
    display: inline-block;
    overflow: hidden;
    margin: 1.25rem 1rem;
    background-color: $clr-background;
    border: 1px solid $clr-primary-50;
    border-radius: 100px;

    @media screen and (min-width: $breakpoint-xs) {
      margin: 1.25rem 1.875rem;
    }

    &--sticky {
      position: sticky;
      top: 240px;
      z-index: 1;
      border: 1px solid $clr-primary;

      @media screen and (max-height: 1000px) {
        top: 90px;
      }
    }
  }

  .tab-header button {
    @include body-large;
    cursor: pointer;
    background-color: $clr-background;
    border: none;
    border-bottom: 2px solid transparent;
    padding: rem(8) 24px;
    color: $clr-support-two-700;

    &.active {
      margin: rem(8);
      background-color: #fff;
      color: $clr-primary-800;
      border-radius: 24px;
      border: 1px solid $clr-primary-50;
      box-shadow: $box-shadow-card;

      &:hover {
        box-shadow: $box-shadow-hover;
      }
    }

    &:not(.active):hover {
      text-decoration: underline;
    }
  }

  .tab-content {
    margin: rem(20) 0;
  }
</style>
